"use client";

import type { ReactNode } from "react";
import Input from "@/components/ui/Input";
import UserPriceInsightPanel from "@/components/ui/UserPriceInsightPanel";
import { usePriceInsight } from "@/lib/hooks/usePriceInsight";
import type { SellPriceBandSnapshot } from "@/lib/hooks/useSellPriceBand";
import { cn } from "@/lib/utils";

interface PriceInputProps {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  error?: string;
  itemTitle: string;
  itemCategory?: string | null;
  itemType?: string | null;
  onSuggestedPrice?: (price: number) => void;
  onClearPrice?: () => void;
  className?: string;
  required?: boolean;
  /** Phase 4: server band enforcement — panel auto-shows band from parent. */
  priceBandEnforcementActive?: boolean;
  sellPriceBand?: SellPriceBandSnapshot | null;
  sellPriceBandLoading?: boolean;
  sellPriceBandError?: string | null;
  /** When true with enforcement, show admin-enabled price recommendation chips. */
  bandRecommendationChipsEnabled?: boolean;
  /**
   * Sell/bulk layout: full-width insight first, then price + secondaryField
   * (e.g. quantity) side-by-side on md+.
   */
  stackedLayout?: boolean;
  secondaryField?: ReactNode;
  /** Structured attrs for attribute-first cohort (e.g. sewing_machine_type). */
  listingAttributes?: Record<string, unknown> | null;
}

/**
 * Price field + optional user price insight (single usePriceInsight instance per mount).
 */
export default function PriceInput({
  value,
  onChange,
  error,
  itemTitle,
  itemCategory,
  itemType,
  onSuggestedPrice,
  onClearPrice,
  className = "",
  required = true,
  priceBandEnforcementActive = false,
  sellPriceBand = null,
  sellPriceBandLoading = false,
  sellPriceBandError = null,
  bandRecommendationChipsEnabled = false,
  stackedLayout = false,
  secondaryField,
  listingAttributes,
}: PriceInputProps) {
  const allowBandPriceApply =
    Boolean(onSuggestedPrice) &&
    (!priceBandEnforcementActive || bandRecommendationChipsEnabled);
  const currentPrice = value ? Number(value.replace(/,/g, "")) : undefined;

  const {
    isEnabled: isPriceInsightEnabled,
    isLoading: priceInsightLoading,
    error: priceInsightError,
    data: priceInsightData,
    getPriceSuggestion,
    clearSuggestion,
  } = usePriceInsight();

  const showPanel = isPriceInsightEnabled && Boolean(itemTitle && itemCategory);

  const insightPanel = showPanel ? (
    <UserPriceInsightPanel
      className={stackedLayout ? "mt-0" : undefined}
      itemTitle={itemTitle}
      itemCategory={itemCategory || ""}
      itemType={itemType || ""}
      currentPrice={currentPrice}
      onSuggestedPrice={allowBandPriceApply ? onSuggestedPrice : undefined}
      onClearPrice={
        priceBandEnforcementActive && !bandRecommendationChipsEnabled
          ? undefined
          : onClearPrice
      }
      bandRecommendationChipsEnabled={bandRecommendationChipsEnabled}
      isEnabled={isPriceInsightEnabled}
      isLoading={priceInsightLoading}
      error={priceInsightError}
      data={priceInsightData}
      getPriceSuggestion={getPriceSuggestion}
      clearSuggestion={clearSuggestion}
      enforcementMode={priceBandEnforcementActive}
      bandLoading={sellPriceBandLoading}
      bandError={sellPriceBandError}
      band={sellPriceBand}
      listingAttributes={listingAttributes}
    />
  ) : null;

  const priceField = (
    <div>
      <label className="block mb-2">
        Price (₦) {required && <span className="text-red-500">*</span>}
      </label>
      <Input
        name="price"
        value={value}
        onChange={onChange}
        error={error}
        required={required}
        type="text"
        placeholder="Enter the price in Naira e.g. 150,000"
        className="w-full"
        maxLength={9}
        aria-invalid={error ? true : undefined}
      />
    </div>
  );

  if (stackedLayout) {
    return (
      <div className={cn("space-y-6", className)}>
        {insightPanel}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {priceField}
          {secondaryField}
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      {priceField}
      {insightPanel ? <div className="mt-2">{insightPanel}</div> : null}
    </div>
  );
}
